import {
    GATE_COVER_PANEL_POSITION, GATE_MOUNT_TYPE, GATE_TYPE, PANEL_TYPE, PANEL_VARIANT, PANEL_FRAME, SOCIAL_APPS,
    DEFAULT_PANEL_CONTENT
} from "./Constants";

// TODO @Darius simplify this
export const CREATE_PANEL_DEFAULT_OPTIONS = {
    // Id or alias of a panel that this one inherits from. Because of versioning, it's highly recommended
    // using an alias here (aliases are stable across versions, IDs are not).
    parentPanel: null,

    type: PANEL_TYPE.custom,

    variant: PANEL_VARIANT.plain,

    // TODO @Mihai this should only be null or array of id/alias
    // Ids or aliases of the subscription offer(s) to use in this panel. If not provided,
    // all subscription offers with visibility=PUBLIC are used.
    subscriptionOffer: null,

    // Ids or aliases of the donation offer(s) to use in this panel. If not provided,
    // all donation offers with visibility=PUBLIC are used.
    // Note: This is for future-compatibility for when we actually support multiple donation offers.
    donationOffer: null,

    // Ids or aliases of the public newsletter(s) to use in this panel. If not provided,
    // all public newsletters are displayed.
    newsletter: null,

    // Ids or aliases of the product(s) to use in this panel. If not provided,
    // all products with visibility=PUBLIC are used.
    products: null,

    // A list of tabs to display in the Inline User Dashboard. The default is to display tabs
    // for all functionality the merchant has enabled.
    dashboardTabs: null,

    // A list of social providers to enable on the authentication page for this panel.
    // Besides the default options below, we also support CDS for merchants with CDS
    // subscriptions/donations backend.
    authSocialProviders: [SOCIAL_APPS.Facebook, SOCIAL_APPS.Google, SOCIAL_APPS.Twitter, SOCIAL_APPS.LinkedIn],

    // Details about the payment. Required if type is "payment".
    // Ignored if type is not "payment".
    // Typescript format of the object:
    // paymentDetails: {
    //   resourceId: String,
    //   amount?: Number,
    //   amountInSubunits?: Number,
    //   comment?: Object,
    // }
    paymentDetails: null,

    // URL of the logo to display at the top of the panel. If this is not
    // provided, no logo is displayed. The URL must be an absolute path
    // to an HTTPS resource.
    logoSrc: null,

    // The ALT text to be applied to the logo image.
    logoAlt: "",

    // Style of the logo displayed at the top of the panel. It supports the format of a Stem styleRule.
    logoStyle: {},

    // Text displayed at the top of the panel. If this is left null, the
    // default differ based on panel type, subscription/donation offer etc.
    text: null,

    // Base style of the text section. It supports the format of a Stem styleRule.
    textStyle: {},

    // Some panels have titles that can be overwritten.
    title: null,

    // Text displayed in the panel button. If this is left null, the
    // default differ based on panel type, subscription/donation offer etc.
    buttonText: null,

    // Base style of the button. It supports the format of a Stem styleRule.
    actionButtonStyle: {},

    // Array of font faces. The format of a font face object is:
    /*{
        family: String,
        src: String,
        style?: String,
        weight?: Number
        unicodeRange?: String,
    }*/
    fonts: [],

    // Overrides for Stem's Theme properties.
    theme: {},

    // Overrides for the Messages object values.
    messages: {},

    // TODO @version rename "metadata" in panels to conversionMetadata
    // Key-value metadata to be saved for conversions (e.g. newsletter
    // registrations, donations, subscriptions etc.)
    metadata: {},

    content: null,

    context: null,

    frame: PANEL_FRAME.logo,

    // Supported values are available in the GATE_TYPE enum.
    gateType: GATE_TYPE.null,

    // Extra style to apply to the gate element itself.
    customStyle: {},

    // Extra style to apply to the cancel button.
    cancelButtonCustomStyle: {},

    // Height (in px) of the preview area. Only applicable for type
    // "preview".
    previewHeight: 200,

    // Enable a fade-out effect between the content and the panel. Only
    // applicable for type "preview".
    previewFadeOut: true,

    // Height (in px) of the fade-out zone of the preview area. Only
    // applicable for type "preview", if "previewFadeOut" is enabled.
    previewFadeOutHeight: 100,

    // Color of the bottom of the fade-out area. Only applicable for type
    // "preview", if "previewFadeOut" is enabled.
    previewFadeOutToColor: "white",

    // Color of the cover effect of the gate. Only applicable for type
    // "cover".
    coverColor: "rgba(255, 255, 255, 0.9)",

    // Position of the panel with regards to the gate (which covers the
    // whole area of the content in this case). Only applicable for type
    // "cover".
    coverPanelPosition: GATE_COVER_PANEL_POSITION.center,

    mountType: GATE_MOUNT_TYPE.lastChild,

    closeButton: true,

    scrimClickCollapse: true,

    // TODO: Add scrimBlockScroll

    // Callbacks

    onMount: null,

    onFlowCheckpoint: null,

    onFlowSuccess: null,

    onFlowFinish: null,
};

// All the options in CREATE_PANEL_DEFAULT_OPTIONS are evaluated through the context.
// The only exceptions are the parentPanel (which influences inheritance of options, and
// having it be dynamic is very weird) and the context option itself.
export const CONTEXT_SENSITIVE_OPTIONS = Object.keys(CREATE_PANEL_DEFAULT_OPTIONS).filter(option => ["parentPanel", "context"].indexOf(option) === -1);

// Apply one layer of panel options on top of target options. This modifies the targetOptions
// in-place in addition to returning them, similar to Object.assign.
export function applyPanelOptionsLayer(targetOptions, layerOptions) {
    if (!layerOptions) {
        return;
    }
    // Fonts, theme constants, messages, metadata and contexts are merged between the options.
    Object.assign(targetOptions, layerOptions, {
        fonts: [
            ...(targetOptions.fonts || []),
            ...(layerOptions.fonts || []),
        ],
        theme: {
            ...targetOptions.theme,
            ...layerOptions.theme,
        },
        messages: {
            ...targetOptions.messages,
            ...layerOptions.messages,
        },
        metadata: {
            ...targetOptions.metadata,
            ...layerOptions.metadata,
        },
        context: {
            ...targetOptions.context,
            ...layerOptions.context,
        },
    });
    return targetOptions;
}

// TODO @Darius @Denis call this in fewer places.
// Given the "givenPanelOptions" from a call to blinkSDK.createPanel(),
// this function returns the options that are actually used by the PanelModule to
// create the panel itself, after going through all the layers of inheritance.
export function composePanelOptions(givenPanelOptions, merchantPanelStore, globalVariables={}) {
    const panelOptions = {
        fonts: [],
        theme: {},
        messages: {},
        metadata: {},
        context: {},
    };

    const optionsLayers = [];

    let panelId = givenPanelOptions.panel;
    while (panelId != null) {
        const panel = merchantPanelStore.find(panel => panel.id == panelId || panel.alias == panelId);
        if (panel == null) {
            break;
        }
        optionsLayers.push(panel.options);
        panelId = panel.options.parentPanel;
    }
    // If we didn't find any panels in the inheritance chain, the "panel" option may be a panel
    // type instead. This behaviour is here to support legacy journeys, but it may not be so bad to
    // keep this fallback even if we fix those.
    if (optionsLayers.length === 0 && !givenPanelOptions.type && Object.values(PANEL_TYPE).indexOf(givenPanelOptions.panel) >= 0) {
        givenPanelOptions.type = givenPanelOptions.panel;
        delete givenPanelOptions.panel;
    }

    // Beyond the inheritance chain defined by the panel, there are two "special" panels we inherit from:
    // the base panel of our type and the global base panel.
    const basePanelOfType = merchantPanelStore.find(panel => panel.alias === givenPanelOptions.type);
    if (givenPanelOptions.type !== PANEL_TYPE.custom && basePanelOfType) {
        optionsLayers.push(basePanelOfType.options);
    }
    const basePanel = merchantPanelStore.find(panel => panel.alias === "base");
    if (basePanel) {
        optionsLayers.push(basePanel.options);
    }
    optionsLayers.push(CREATE_PANEL_DEFAULT_OPTIONS);

    for (const layer of optionsLayers.reverse()) {
        applyPanelOptionsLayer(panelOptions, layer);
    }

    // Make the global variables override the panel context inherited from panels and base panels.
    // This is so journeys can set variables in A/B tests, that overwrite panel context.
    // Apply them BEFORE givenPanelOptions, so journeys or manual calls to blinkSDK.createPanel()
    // can still overwrite this context.
    applyPanelOptionsLayer(panelOptions, {
        context: globalVariables,
    });
    applyPanelOptionsLayer(panelOptions, givenPanelOptions);

    if (DEFAULT_PANEL_CONTENT[panelOptions.type] && !panelOptions.content) {
        panelOptions.content = DEFAULT_PANEL_CONTENT[panelOptions.type];
    }
    if (!panelOptions.mountType) {
        panelOptions.mountType = panelOptions.gateType === GATE_TYPE.null ?
            GATE_MOUNT_TYPE.replaceChildren : GATE_MOUNT_TYPE.lastChild;
    }
    delete panelOptions.parentPanel;
    return panelOptions;
}
